import BaseHttpService from "./https.service";

/**
 * Single consolidated service for the retention messaging surface.
 * All requests route through config-manager (`/manager/...`) where Okta auth + RBAC
 * are enforced. Apple is talked to from retention-messaging-service; we never call
 * retention-messaging-service directly from the browser.
 */

const isLocal = window.location.href.indexOf('localhost') >= 0;
const retentionBaseUrl = isLocal ? 'http://localhost:5001/manager' : '/manager';

class RetentionService extends BaseHttpService {
  constructor() {
    super(retentionBaseUrl);
  }

  /* ─── Messages ─── */

  getAllMessages() {
    return this.get('/retention/message/all');
  }
  getMessage(messageId) {
    return this.get('/retention/message/item', { messageId });
  }
  addMessage(payload) {
    return this.post('/retention/message/add', payload);
  }
  editMessage(id, data) {
    return this.post('/retention/message/edit', { id, data });
  }
  submitMessage(id) {
    return this.post('/retention/message/submit', { id });
  }
  deleteMessage(id) {
    return this.post('/retention/message/delete', { id });
  }
  duplicateMessageToLanguages(messageId, targetLanguages) {
    return this.post('/retention/message/duplicate-languages', { messageId, targetLanguages });
  }
  shortenSkippedTranslations(messageId, items) {
    return this.post('/retention/message/shorten-languages', { messageId, items });
  }

  /* ─── Groups ─── */

  getAllGroups() {
    return this.get('/retention/group/all');
  }
  addMessageToGroup({ messageId, targetMessageId }) {
    return this.post('/retention/message/add-to-group', { messageId, targetMessageId });
  }
  deleteGroup(groupId) {
    return this.post('/retention/message/delete-group', { groupId });
  }

  /* ─── Images ─── */

  getAllImages() {
    return this.get('/retention/image/all');
  }
  addImage({ image, name, description, usage }) {
    return this.post('/retention/image/add', { image, name, description, usage });
  }
  editImage(id, data) {
    return this.post('/retention/image/edit', { id, data });
  }
  submitImage(id) {
    return this.post('/retention/image/submit', { id });
  }
  deleteImage(id) {
    return this.post('/retention/image/delete', { id });
  }

  /* ─── Defaults ─── */

  getAllDefaults() {
    return this.get('/retention/default/all');
  }
  getProducts() {
    return this.get('/retention/default/products');
  }
  getRealtimeProducts() {
    return this.get('/retention/realtime/products');
  }
  configureDefault({ productId, locale, messageIdentifier }) {
    return this.post('/retention/default/configure', {
      productId,
      locale,
      messageIdentifier
    });
  }
  removeDefault({ productId, locale }) {
    return this.post('/retention/default/delete', { productId, locale });
  }

  /* ─── Admin (gated by admin.all on the backend) ─── */
  /*
   * Admin operations are per-app (Apple stores realtime URL + perf-test state
   * keyed by bundle id). The /admin page is app-agnostic, so the panel passes
   * an explicit appId picked from a dropdown instead of relying on activeApp.
   */

  getRetentionConfiguration(appId) {
    return this.axiosInstance.get('/retention/admin/configuration', { params: { aid: appId } });
  }
  getRetentionConfigStatus(appId) {
    return this.axiosInstance.get('/retention/configuration/status', { params: { aid: appId } });
  }
  getRealtimeUrl(appId) {
    return this.axiosInstance.get('/retention/admin/realtime-url', { params: { aid: appId } });
  }
  configureRealtimeUrl(appId, { realtimeURL }) {
    return this.axiosInstance.post('/retention/admin/realtime-url/configure', { realtimeURL }, { params: { aid: appId } });
  }
  deleteRealtimeUrl(appId) {
    return this.axiosInstance.post('/retention/admin/realtime-url/delete', {}, { params: { aid: appId } });
  }
  runPerformanceTest(appId, { originalTransactionId }) {
    return this.axiosInstance.post('/retention/admin/performance-test/start', { originalTransactionId }, { params: { aid: appId } });
  }
  getPerformanceTestResult(appId, requestId) {
    return this.axiosInstance.get(
      `/retention/admin/performance-test/${encodeURIComponent(requestId)}`,
      { params: { aid: appId } }
    );
  }
}

export const retentionService = new RetentionService();
